import { useLocation } from "react-router-dom";
import { Bell, Search, ChevronDown, LogOut, UserCircle2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

const PAGE_TITLES = {
  "/": "Executive Dashboard",
  "/campaigns": "Campaigns",
  "/curve-library": "Curve Library",
  "/upload-curve": "Upload Curve Files",
  "/vendors": "Vendor Parameters",
  "/ddf": "De-Duplication Matrix",
  "/budget-sensitivity": "Budget Sensitivity",
  "/optimizer": "Optimizer",
  "/reports": "Reports",
  "/settings": "Settings",
};

export default function Topbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const title = PAGE_TITLES[loc.pathname] || "HARMONY";

  return (
    <header
      data-testid={TID.topbar}
      className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30"
    >
      <div className="flex items-center gap-6">
        <h1 className="font-display text-xl font-semibold text-slate-900">{title}</h1>
      </div>

      <div className="flex items-center gap-3">
        <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg w-72">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            placeholder="Search campaigns, curves..."
            className="bg-transparent outline-none text-sm w-full placeholder:text-slate-400"
          />
        </div>

        <button
          className="relative h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600"
          data-testid="topbar-notifications"
        >
          <Bell className="h-4 w-4" />
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#18B7D8]" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={TID.userMenu}
              className="flex items-center gap-2 pl-2 pr-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
            >
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#0A3D91] to-[#1CC7B3] text-white flex items-center justify-center text-xs font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-xs font-semibold text-slate-900 leading-tight">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</div>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-display">
              <div className="text-sm">{user?.name}</div>
              <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer">
              <UserCircle2 className="h-4 w-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid={TID.logoutBtn}
              className="cursor-pointer text-rose-600 focus:text-rose-600"
              onClick={logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
